import { ref, watch } from "vue"
import { getCachedProfilePicture } from "@/utils/signedUrlCache"
import { useProfile } from "./useProfile"

// Simple event emitter for profile picture updates
const refreshTrigger = ref(0)
const cachedProfilePictureUrl = ref<string | null>(null)
const isLoading = ref(false)

export function useProfilePictureRefresh() {
  const { profileData, loaded } = useProfile()

  function triggerRefresh() {
    refreshTrigger.value++
  }

  /**
   * Load profile picture from the cached profile data
   * Does NOT fetch profile data - relies on auto-initialization from useProfile()
   */
  async function loadProfilePicture(userId?: string): Promise<string | null> {
    if (!userId) {
      cachedProfilePictureUrl.value = null
      return null
    }

    isLoading.value = true

    try {
      // Wait for profile to be loaded (auto-initialized by useProfile)
      // This doesn't trigger a fetch - just waits for existing initialization
      const maxWait = 50 // 50 attempts × 100ms = 5 seconds max
      let attempts = 0
      while (!loaded.value && attempts < maxWait) {
        await new Promise((resolve) => setTimeout(resolve, 100))
        attempts++
      }

      // If still not loaded after timeout, return null
      if (!profileData.value) {
        cachedProfilePictureUrl.value = null
        return null
      }

      // If no profile picture, return null
      if (!profileData.value.profile_pic_url) {
        cachedProfilePictureUrl.value = null
        return null
      }

      // Get signed URL using the cache
      const signedUrl = await getCachedProfilePicture(profileData.value.profile_pic_url)

      cachedProfilePictureUrl.value = signedUrl
      return signedUrl
    } catch (error) {
      logError("Error loading profile picture:", error)
      cachedProfilePictureUrl.value = null
      return null
    } finally {
      isLoading.value = false
    }
  }

  // Watch for changes to profileData and update cached URL
  watch(
    () => profileData.value?.profile_pic_url,
    async (newProfilePicUrl) => {
      if (profileData.value && newProfilePicUrl) {
        const signedUrl = await getCachedProfilePicture(newProfilePicUrl)
        cachedProfilePictureUrl.value = signedUrl
      } else {
        cachedProfilePictureUrl.value = null
      }
    },
    { immediate: true },
  )

  return {
    refreshTrigger,
    cachedProfilePictureUrl,
    isLoading,
    triggerRefresh,
    loadProfilePicture,
  }
}
import { error as logError } from "@/utils/debug"
